import React, { useState, useRef } from 'react';
import { Drawer } from '../../../../components';
import style from './add-adaptive-card.module.scss';
import { useMutate, useAppAndBranchId } from '../../../../hooks';
import { api } from '../../api';
import { useToast } from '../../../../../../../../helpers/commonFunction';
import BracesIcon from '../../../../../../../../assets/svg/solo-chat/braces.svg';
import Editor from '@monaco-editor/react';
import { ContentMode } from '../../../../types';
import { validateList } from '../../../../utils/validation';
import { errorMessage } from '../../../../utils/error';

type AdaptiveCard = {
	title: string;
	payload: Record<string, any>;
};

type Props = {
	isOpen: boolean;
	close: () => void;
	mode?: ContentMode;
	initialValue?: AdaptiveCard & { id: string };
	refetchContent: () => void;
	onCreate?: (contentId: string) => void;
	isPreview?: boolean
};

export function AddAdaptiveCard(props: Props) {
	const editorRef = useRef<any>(null)
	const { ErrorToast, WarningToast } = useToast();
	const [appId, branchId] = useAppAndBranchId();
	const { isOpen, close, initialValue, refetchContent, onCreate, isPreview = false } = props;
	const [state, setState] = useState<AdaptiveCard>(
		initialValue ?? {
			title: '',
			payload: {},
		},
	);
	const [mode, setMode] = useState(props.mode ?? 'create');

	const { mutate: createMutate, loading: createLoading } = useMutate(
		api.createContent,
	);
	const { mutate: updateMutate, loading: updateLoading } = useMutate(
		api.updateContent,
	);

	const [error, setError] = useState({
		title: '',
		payload: '',
	});

  function clearError(key: keyof typeof error) {
	  setError(prev => ({...prev, [key]: ""}))
	}

	function validateAdaptiveCard() {
		const payload = editorRef.current.getValue();
		const result = validateList([
			{
				name: 'title',
				value: state.title,
				rules: [
					{
						message: "Title can't be empty",
						fn: (value: string) => value.trim().length <= 0,
					},
				],
			},
			{
				name: 'payload',
				value: payload,
				rules: [
					{
						message: "JSON value can't be empty",
						fn: (value: string) => value.trim().length <= 0,
					},
					{
						message: "Please enter valid JSON",
						fn: (value: string) => {
							try {
								JSON.parse(value)
								return false
							} catch {
								return true
							}
						},
					},
					{
						message: "Invalid Adaptive Card schema. Missing 'template' field",
						fn: (value: string) => {
							try {
								const result = JSON.parse(value)
								if(!("template" in result)) {
									return true
								} else {
									return false
								}
							} catch {
								return true
							}
						},
					}
				],
			},
		]);
		const hasError = Object.keys(result).length !== 0
		if(hasError) {
		  setError(prev => ({ ...prev, ...result }))
		}
		if(result.payload) {
		  WarningToast(result.payload)
		}
		return !hasError

	}

 function formatJSON(val: Record<string, any>) {
		try {
			return JSON.stringify(val, null, 2)
		} catch {
			return ""
		}
	}

	return (
		<Drawer
			isPreview={isPreview}
			mode={mode}
			loading={createLoading || updateLoading}
			visible={isOpen}
			contentType="adaptiveCard"
			onClose={() => {
				setState({
					title: '',
					payload: {}
				});
				close();
			}}
			onSave={async () => {
				if (mode !== 'view' && !validateAdaptiveCard()) {
					return;
				}
				const editorValue = editorRef.current.getValue()
				try {
					JSON.parse(editorValue)
				} catch(err) {
					WarningToast("Invalid JSON found")
					return
				}
				const payload = {
					branchId,
					contentType: 'adaptiveCard',
					contentValue: {
						title: state.title.trim(),
						payload: JSON.parse(editorValue) 
					},
				};
				try {
					if (mode === 'edit') {
						await updateMutate(appId, initialValue?.id, payload);
					} else if (mode === 'create') {
						const res = await createMutate(appId, payload);
						if (onCreate) {
							onCreate(res.contentId);
						}
					} else {
						setMode('edit');
						return;
					}
					refetchContent();
					close();
				} catch (err) {
          ErrorToast(errorMessage(mode, 'adaptive card', err.code))
				}
			}}
		>
			<div className={style.addAdaptiveCard}>
				<div style={{ position: "relative" }}>
					<label htmlFor="title">Title</label>
					<input value={state.title} disabled={mode === "view"} autoFocus id="title" placeholder="Enter title" onChange={e => {
					  clearError("title")
						const title = e.currentTarget.value
						setState(prev => ({...prev, title}))
					}} />
					<span className={style.error}>{error.title}</span>
				</div>
				<div style={{ marginTop: error.title ? '8px' : '0' }}>
					<div className={style.labelWrapper}>
						<img src={BracesIcon} alt="code" />
						<label>Raw JSON</label>
					</div>
					<div className={style.codeEditorWrapper}>
						<Editor
							options={{
								minimap: {
									enabled: false,
								},
								tabSize: 2,
								fontSize: 14,
								lineNumbers: 'off',
								scrollBeyondLastLine: false,
								readOnly: mode === "view",
								renderLineHighlight: 'none',
								glyphMargin: false,
								folding: false,
								lineDecorationsWidth: 0,
								lineNumbersMinChars: 0,
							  formatOnPaste: true,
							}}
							value={formatJSON(state.payload)}
							language="json"
							theme='light'
							keepCurrentModel={false}
							width="100%"
							onChange={() => {
							  clearError("payload")
							}}
							onMount={(editor) => {
								editorRef.current = editor
							}}
						/>
					</div>
				</div>
			</div>
		</Drawer>
	);
}
